import { fetchOptions } from './csrf'

export interface AccountListItem {
  id: string
  legacy_id: number | null
  name: string
  email: string
  phone: string | null
  domain: string | null
  account_type: 'group' | 'rooftop'
  active: boolean
  is_on_billing_hold: boolean
  deleted_at: string | null
  parent_name: string | null
  users_count: number
  created_at: string
}

export interface AccountDetail {
  id: string
  legacy_id: number | null
  name: string
  email: string
  phone: string | null
  domain: string | null
  account_type: 'group' | 'rooftop'
  active: boolean
  is_on_billing_hold: boolean
  deleted_at: string | null
  parent_id: string | null
  parent_name: string | null
  children: { id: string; name: string; active: boolean }[]
  users_count: number
  deactivated_at: string | null
  deactivated_by_id: string | null
  deactivation_reason: string | null
  created_at: string
  updated_at: string
}

export interface AccountUser {
  id: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  phone: string | null
  cell_phone: string | null
  roles: { id: string; name: string }[]
  last_sign_in_at: string | null
  sign_in_count: number
  deleted: boolean
  created_at: string
}

export interface StaffNote {
  id: string
  body: string
  staff_id: string
  staff_name: string
  created_at: string
  updated_at: string
}

export interface FeatureItem {
  id: string
  key: string
  name: string
  description: string | null
  category: string
  is_default: boolean
  is_visible: boolean
  enabled?: boolean
  created_at: string
  updated_at: string
}

export interface SalesforceAccount {
  sf_id: string
  name: string
  account_number: string | null
  phone: string | null
  website: string | null
  city: string | null
  state: string | null
}

export interface SalesforceContact {
  sf_id: string
  first_name: string
  last_name: string
  email: string | null
  phone: string | null
  title: string | null
}

export interface CreateAccountUser {
  email: string
  first_name: string
  last_name: string
  role_names: string[]
}

export interface CreateAccountPayload {
  account: {
    name: string
    email: string
    phone: string
    domain?: string
    account_type: 'group' | 'rooftop'
    parent_id?: string
  }
  users: CreateAccountUser[]
  salesforce_account_id?: string
}

export interface CreateAccountResponse {
  account: AccountDetail
  users: { id: string; email: string; first_name: string; last_name: string }[]
}

export interface AccountsResponse {
  accounts: AccountListItem[]
  meta: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export interface AccountSearchParams {
  q?: string
  status?: 'active' | 'inactive' | 'deleted' | ''
  account_type?: 'group' | 'rooftop' | ''
  billing_hold?: 'true' | ''
  sort?: string
  direction?: 'asc' | 'desc'
  page?: number
  per_page?: number
}

function buildQueryString(params: AccountSearchParams): string {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      searchParams.set(key, String(value))
    }
  })
  return searchParams.toString()
}

export interface SystemHealthData {
  overview: {
    processed: number
    failed: number
    enqueued: number
    scheduled: number
    retries: number
    dead: number
  }
  queues: { name: string; size: number; latency: number }[]
  workers: {
    hostname: string
    pid: number
    started_at: string
    queues: string[]
    busy: number
    concurrency: number
  }[]
  workers_count: number
  busy_count: number
  failed_jobs: {
    jid: string
    queue: string
    class: string
    error_class: string | null
    error_message: string | null
    failed_at: string | null
    retry_count: number
  }[]
  redis: {
    connected: boolean
    used_memory_human: string
    connected_clients: number
    uptime_in_days?: number
  }
  timestamp: string
}

export interface WebsiteSettings {
  id: string | null
  account_id: string
  google_analytics_id: string | null
  google_tag_manager_id: string | null
  primary_color: string | null
  secondary_color: string | null
  template_name: string | null
  custom_head_html: string | null
  custom_footer_html: string | null
  mobile_site_enabled: boolean
  created_at: string | null
  updated_at: string | null
}

export interface DomainItem {
  id: string
  account_id: string
  domain: string
  is_primary: boolean
  dns_status: 'dns_pending' | 'dns_active' | 'dns_error'
  ssl_status: 'ssl_pending' | 'ssl_active' | 'ssl_error'
  created_at: string
  updated_at: string
}

export interface ReportsSummaryData {
  accounts: {
    total_active: number
    total_inactive: number
    total_deleted: number
    new_this_month: number
    groups: number
    rooftops: number
    on_billing_hold: number
  }
  users: {
    total_active: number
    new_this_month: number
    signed_in_this_month: number
  }
  staff: {
    total: number
  }
  generated_at: string
}

export const controlCenterApi = {
  async getAccounts(params: AccountSearchParams = {}): Promise<AccountsResponse> {
    const qs = buildQueryString(params)
    const url = `/api/v1/staff/accounts${qs ? `?${qs}` : ''}`
    const response = await fetch(url, fetchOptions())
    if (!response.ok) {
      throw new Error('Failed to fetch accounts')
    }
    return response.json()
  },

  async getAccount(id: string): Promise<AccountDetail> {
    const response = await fetch(`/api/v1/staff/accounts/${id}`, fetchOptions())
    if (!response.ok) {
      throw new Error('Failed to fetch account')
    }
    const data = await response.json()
    return data.account
  },

  async getAccountUsers(accountId: string): Promise<AccountUser[]> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/users`, fetchOptions())
    if (!response.ok) {
      throw new Error('Failed to fetch account users')
    }
    const data = await response.json()
    return data.users
  },

  async getAccountNotes(accountId: string): Promise<StaffNote[]> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/notes`, fetchOptions())
    if (!response.ok) {
      throw new Error('Failed to fetch notes')
    }
    const data = await response.json()
    return data.notes
  },

  async createAccountNote(accountId: string, body: string): Promise<StaffNote> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/notes`, {
      ...fetchOptions('POST'),
      body: JSON.stringify({ body }),
    })
    if (!response.ok) {
      throw new Error('Failed to create note')
    }
    const data = await response.json()
    return data.note
  },

  async updateAccountNote(accountId: string, noteId: string, body: string): Promise<StaffNote> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/notes/${noteId}`, {
      ...fetchOptions('PATCH'),
      body: JSON.stringify({ body }),
    })
    if (!response.ok) {
      throw new Error('Failed to update note')
    }
    const data = await response.json()
    return data.note
  },

  async deleteAccountNote(accountId: string, noteId: string): Promise<void> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/notes/${noteId}`, fetchOptions('DELETE'))
    if (!response.ok) {
      throw new Error('Failed to delete note')
    }
  },

  async activateAccount(id: string): Promise<AccountDetail> {
    const response = await fetch(`/api/v1/staff/accounts/${id}/activate`, {
      ...fetchOptions('PUT'),
      body: JSON.stringify({}),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: 'Failed to activate account' }))
      throw new Error(data.error || 'Failed to activate account')
    }
    const data = await response.json()
    return data.account
  },

  async deactivateAccount(id: string, reason: string): Promise<AccountDetail> {
    const response = await fetch(`/api/v1/staff/accounts/${id}/deactivate`, {
      ...fetchOptions('PUT'),
      body: JSON.stringify({ reason }),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: 'Failed to deactivate account' }))
      throw new Error(data.error || 'Failed to deactivate account')
    }
    const data = await response.json()
    return data.account
  },

  async reactivateAccount(id: string, salesforceAccountId?: string): Promise<AccountDetail> {
    const body: Record<string, string> = {}
    if (salesforceAccountId) body.salesforce_account_id = salesforceAccountId
    const response = await fetch(`/api/v1/staff/accounts/${id}/reactivate`, {
      ...fetchOptions('PUT'),
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: 'Failed to reactivate account' }))
      throw new Error(data.error || 'Failed to reactivate account')
    }
    const data = await response.json()
    return data.account
  },

  async createAccount(payload: CreateAccountPayload): Promise<CreateAccountResponse> {
    const response = await fetch('/api/v1/staff/accounts', {
      ...fetchOptions('POST'),
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ errors: ['Failed to create account'] }))
      throw new Error(data.errors?.[0] || 'Failed to create account')
    }
    return response.json()
  },

  async searchSalesforceAccounts(params: { q?: string; number?: string }): Promise<SalesforceAccount[]> {
    const searchParams = new URLSearchParams()
    if (params.q) searchParams.set('q', params.q)
    if (params.number) searchParams.set('number', params.number)
    const response = await fetch(`/api/v1/staff/salesforce/accounts?${searchParams}`, fetchOptions())
    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: 'SalesForce search failed' }))
      throw new Error(data.error || 'SalesForce search failed')
    }
    const data = await response.json()
    return data.accounts
  },

  async getSalesforceContacts(accountId: string): Promise<SalesforceContact[]> {
    const response = await fetch(`/api/v1/staff/salesforce/contacts?account_id=${encodeURIComponent(accountId)}`, fetchOptions())
    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: 'Failed to fetch contacts' }))
      throw new Error(data.error || 'Failed to fetch contacts')
    }
    const data = await response.json()
    return data.contacts
  },

  async getGroupAccounts(): Promise<AccountListItem[]> {
    const response = await fetch('/api/v1/staff/accounts?account_type=group&status=active&per_page=100', fetchOptions())
    if (!response.ok) {
      throw new Error('Failed to fetch group accounts')
    }
    const data = await response.json()
    return data.accounts
  },

  async toggleBillingHold(id: string, enabled: boolean): Promise<AccountDetail> {
    const response = await fetch(`/api/v1/staff/accounts/${id}/billing_hold`, {
      ...fetchOptions('PUT'),
      body: JSON.stringify({ enabled }),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: 'Failed to update billing hold' }))
      throw new Error(data.error || 'Failed to update billing hold')
    }
    const data = await response.json()
    return data.account
  },

  async exportAccountsCsvUrl(params: AccountSearchParams = {}): Promise<string> {
    const qs = buildQueryString(params)
    return `/api/v1/staff/accounts/export${qs ? `?${qs}` : ''}`
  },

  async getAccountFeatures(accountId: string): Promise<FeatureItem[]> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/features`, fetchOptions())
    if (!response.ok) throw new Error('Failed to fetch features')
    const data = await response.json()
    return data.features
  },

  async toggleFeature(accountId: string, featureId: string): Promise<{ enabled: boolean; feature: FeatureItem }> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/features/${featureId}/toggle`, fetchOptions('PUT'))
    if (!response.ok) throw new Error('Failed to toggle feature')
    return response.json()
  },

  async getWebsiteSettings(accountId: string): Promise<WebsiteSettings> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/website_settings`, fetchOptions())
    if (!response.ok) throw new Error('Failed to fetch website settings')
    const data = await response.json()
    return data.website_setting
  },

  async updateWebsiteSettings(accountId: string, settings: Partial<WebsiteSettings>): Promise<WebsiteSettings> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/website_settings`, {
      ...fetchOptions('PUT'),
      body: JSON.stringify({ website_setting: settings }),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ errors: ['Failed to save settings'] }))
      throw new Error(data.errors?.[0] || 'Failed to save settings')
    }
    const data = await response.json()
    return data.website_setting
  },

  async getDomains(accountId: string): Promise<DomainItem[]> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/domains`, fetchOptions())
    if (!response.ok) throw new Error('Failed to fetch domains')
    const data = await response.json()
    return data.domains
  },

  async addDomain(accountId: string, domain: string): Promise<DomainItem> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/domains`, {
      ...fetchOptions('POST'),
      body: JSON.stringify({ domain: { domain } }),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ errors: ['Failed to add domain'] }))
      throw new Error(data.errors?.[0] || 'Failed to add domain')
    }
    const data = await response.json()
    return data.domain
  },

  async removeDomain(accountId: string, domainId: string): Promise<void> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/domains/${domainId}`, fetchOptions('DELETE'))
    if (!response.ok) {
      const data = await response.json().catch(() => ({ errors: ['Failed to remove domain'] }))
      throw new Error(data.errors?.[0] || 'Failed to remove domain')
    }
  },

  async setDomainPrimary(accountId: string, domainId: string): Promise<DomainItem> {
    const response = await fetch(`/api/v1/staff/accounts/${accountId}/domains/${domainId}/set_primary`, fetchOptions('PUT'))
    if (!response.ok) throw new Error('Failed to set primary domain')
    const data = await response.json()
    return data.domain
  },

  async getReportsSummary(): Promise<ReportsSummaryData> {
    const response = await fetch('/api/v1/staff/reports/summary', fetchOptions())
    if (!response.ok) throw new Error('Failed to fetch reports summary')
    const data = await response.json()
    return data.report
  },

  async getSystemHealth(): Promise<SystemHealthData> {
    const response = await fetch('/api/v1/staff/system_health', fetchOptions())
    if (!response.ok) throw new Error('Failed to fetch system health')
    const data = await response.json()
    return data.system_health
  },
}
